import cartApi from "@api/cart";
import { ICartAddRemoveItemInput, ICartProductInput } from "@types";
import { COOKIE_EVENT_TIMER } from "@utils/constants";
import { getCookie } from "@utils/Cookies";
import { toFarsiNumber } from "@utils/digits";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { CartAction, CartContextType } from "types/cart";
import { useAppContext } from "./app";
import { useUI } from "./ui-managed-context";
import { useGAEvent } from "@shared/hooks/useGAEvent";

const initialState: CartContextType = {
  cart: [],
  setCartItems: () => {},
  addItem: async () => {
    return true;
  },
  removeItem: async () => {
    return true;
  },
  preRegister: async () => {
    return true;
  },
  registerFree: async () => {
    return true;
  },
  prices: {
    total_price: 0,
    total_discount: 0,
    total_price_after_discount: 0,
  },
};

const CartContext = createContext(initialState);

function appReducer(state: CartContextType, action: CartAction) {
  switch (action.type) {
    case "SET_CART_ITEMS": {
      return {
        ...state,
        cart: action.payload,
      };
    }
    case "REMOVE_CART_ITEM": {
      const idx = state.cart.findIndex((f) => f.cart_hash === action.payload);
      if (idx > -1) {
        const s = [...state.cart];
        s.splice(idx, 1);
        return {
          ...state,
          cart: s,
        };
      }
      return state;
    }
    default:
      return state;
  }
}

CartContext.displayName = "CartContext";

const CartProvider: React.FC<{ children?: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const {
    user: { user_id },
  } = useAppContext();
  const { GaAddToCartEvent } = useGAEvent();
  const {
    closeAlert,
    openAlert,
    setStaticToastView,
    openStaticToast,
    setAlertView,
    alertView,
  } = useUI();
  const cookie = getCookie();
  const setCartItems = useCallback((payload: ICartProductInput[]) => {
    dispatch({ type: "SET_CART_ITEMS", payload });
  }, []);
  const addItem = useCallback(
    async (item: ICartAddRemoveItemInput) => {
      try {
        const res = await cartApi.add(item.id).fetch();
        if (res.status) {
          const fundProduct = res.data.products.find((f) => f.id === item.id);
          if (fundProduct) {
            if (!cookie.get(COOKIE_EVENT_TIMER)) {
              cookie.set(COOKIE_EVENT_TIMER, true, {}, 1 / 24);
            }
            GaAddToCartEvent();
            // addToCartByUser();
            // addToCartEvent({
            //   sku: pr.sku,
            //   price: pr.price,
            //   name: pr.title,
            //   categories: [],
            // });
          }

          dispatch({ type: "SET_CART_ITEMS", payload: res.data.products });

          setStaticToastView("dark", toFarsiNumber(res.message), {});
          openStaticToast();
          return true;
        } else {
          setStaticToastView("danger", res.message);
          openStaticToast();
        }
        return false;
      } catch (error) {
        setStaticToastView(
          "danger",
          (error as any)?.error?.message ||
            (error as any)?.message ||
            (error as any)
        );
        openStaticToast();

        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alertView.alertContent, openAlert, setAlertView]
  );

  const addSnack = (message: string, func?: () => void) => {
    alertView.alertContent.push({
      mode: "default",
      textBody: message,
      submitFunction: func,
    });
    setAlertView("SNACKBAR", alertView.alertContent);
    openAlert();
  };

  const removeItem = useCallback(
    async (item: ICartAddRemoveItemInput) => {
      try {
        // addSnack("آموزش در حال حذف شدن است.");
        const itemsBeforeDelete = state.cart;
        const itemId = item.id;
        const res = await cartApi.remove(itemId).fetch();

        const fundProduct = state.cart.find((f) => f.id === itemId);
        const restoreHandler = async () => {
          const res = await cartApi.add(itemId).fetch();
          if (res)
            dispatch({
              type: "SET_CART_ITEMS",
              payload: itemsBeforeDelete,
            });
          closeAlert();
        };

        if (res.status) {
          // removeFromCartEvent({
          //   sku: item.sku,
          //   price: item.price,
          //   name: item.title,
          //   categories: [],
          // });
          if (fundProduct) {
            dispatch({
              type: "REMOVE_CART_ITEM",
              payload: fundProduct.cart_hash,
            });
          }

          addSnack(toFarsiNumber(res.message), restoreHandler);
          return true;
        } else {
          addSnack(toFarsiNumber(res.message), restoreHandler);
        }
        return false;
      } catch (error) {
        const errorMessage =
          (error as any)?.error?.message ||
          (error as any)?.message ||
          (error as any);
        if (typeof errorMessage === "string")
          addSnack(toFarsiNumber(errorMessage), cartApi.add(item.id).fetch);

        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [openStaticToast, setStaticToastView, state]
  );
  const preRegister = useCallback(
    async (id: number) => {
      try {
        const res = await cartApi.preRegister(id).fetch();
        if (res.status) {
          alertView.alertContent.push({
            mode: "default",
            textBody: res.message,
          });
          setAlertView("SNACKBAR", alertView.alertContent);
          openAlert();
          return true;
        } else {
          alertView.alertContent.push({
            mode: "default",
            textBody: res.message,
          });
          setAlertView("SNACKBAR", alertView.alertContent);
          openAlert();
        }
        return false;
      } catch (error) {
        const errorMessage =
          (error as any)?.error?.message ||
          (error as any)?.message ||
          (error as any);
        if (typeof errorMessage === "string") {
          alertView.alertContent.push({
            mode: "danger",
            textBody: errorMessage,
          });
          setAlertView("SNACKBAR", alertView.alertContent);
          openAlert();
        }

        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alertView.alertContent, openAlert, setAlertView]
  );
  const registerFree = useCallback(
    async (id: number) => {
      try {
        const res = await cartApi.registerFree(id).fetch();
        if (res.status) {
          // event({
          //   category: "Users",
          //   action: "Free Course",
          //   label: (user_id || "").toString(),
          // });
          setStaticToastView("success", res.message);
          openStaticToast();
          return true;
        } else {
          setStaticToastView("danger", res.message);
          openStaticToast();
          return false;
        }
      } catch (error) {
        setStaticToastView(
          "danger",
          (error as any)?.error?.message ||
            (error as any)?.message ||
            (error as any)
        );
        openStaticToast();
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [alertView.alertContent, user_id, openAlert, setAlertView]
  );

  const prices = useMemo(() => {
    let total_price = 0;
    let total_discount = 0;

    state.cart.forEach((cartItem) => {
      total_price += cartItem.price;
      if (cartItem.price_festival)
        total_discount += cartItem.price - cartItem.price_after_discount;
    });

    return {
      total_price,
      total_discount,
      total_price_after_discount: total_price - total_discount,
    };
  }, [state.cart]);

  const value = useMemo(
    () => ({
      ...state,
      setCartItems,
      addItem,
      removeItem,
      preRegister,
      registerFree,
      prices,
    }),
    [state, registerFree, preRegister, removeItem, addItem, setCartItems]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export function useCartContext() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error(`useCartContext must be used within a CartProvider`);
  }
  return context;
}

export default CartProvider;
